import { RouteConfigurationServiceDomain } from './domain';

export class CallbackURLSanitizer {
    /**
     * Determines whether a callbackURL for OAuth Authorization Code Flow is safe to redirect to after authentication.
     * @param callbackURL
     * @returns
     */
    public isSafeCallbackUrl(callbackURL: string | null): callbackURL is string {
        if (!callbackURL) {
            return false;
        }

        const pathToCheck = this.getRelativePath(callbackURL);

        /**
         * Cannot redirect to the signin bounce page because it will cause an infinite loop.
         */
        if (pathToCheck === RouteConfigurationServiceDomain.ApplicationRoute.AUTH_SIGNIN_BOUNCE) {
            return false;
        }

        const isAllowedExternal =
            RouteConfigurationServiceDomain.ALLOWED_EXTERNAL_CALLBACK_URLS.includes(
                pathToCheck as RouteConfigurationServiceDomain.ExternalRoute
            ) ||
            // Cover terminating slash case
            RouteConfigurationServiceDomain.ALLOWED_EXTERNAL_CALLBACK_URLS.includes(
                `${pathToCheck}/` as RouteConfigurationServiceDomain.ExternalRoute
            );
        const isInternalRoute = pathToCheck.startsWith('/homeloans/mortgage/');

        return isAllowedExternal || isInternalRoute;
    }

    /**
     * Returns a safe callback URL for OAuth Authorization Code Flow, given an unknown callback URL, handling default fallbacks
     * @param callbackURL - The unknown callback URL.
     * @returns The safe callback URL.
     */
    public getSafeCallbackUrl(callbackURL: string | null): string {
        if (!this.isSafeCallbackUrl(callbackURL)) {
            return RouteConfigurationServiceDomain.ApplicationRoute.DASHBOARD_BOUNCE;
        }
        return this.getRelativePath(callbackURL);
    }

    /**
     * Returns the relative path of a callback URL, handling both relative and full URLs.
     * Preserves query parameters and hash fragments.
     * @param callbackURL - The callback URL to get the relative path of.
     * @returns The relative path of the callback URL with query parameters and hash fragments.
     */
    public getRelativePath(callbackURL: string): string {
        try {
            const url = new URL(callbackURL);
            return url.pathname + url.search + url.hash;
        } catch {
            return callbackURL;
        }
    }
}
